import {
  useQuery,
  useMutation,
  useQueryClient,
  QueryClient,
} from '@tanstack/react-query';

import {
  getTeammatesListForSelectionService,
  getTeammatesListService,
  getTeammateDetailsService,
  reactivateTeammateService,
  resetTeammatePasswordService,
  startTeammateUpdateService,
  changeTeammateEmailService,
  changeTeammatePhoneService,
  lockTeammateService,
  unlockTeammateService,
  deactivateTeammateService,
  addTeammateService,
  editTeammateService,
} from '@/services/teammates';
import {
  queryKeys,
  TeammateFormFields,
  TeammateForSelectionQueryParams,
  TeammateListQueryParams,
} from '@/types/teammates';
import { convertObjectValuesToString } from '@/utils/responseConvertors';
import { SelectOption } from '@/types/inputs';
import { useSearchParams } from 'react-router-dom';
import {
  UpdateEmailFields,
  UpdatePasswordFields,
  UpdatePhoneFields,
} from '@/types/common';
import useIdempotencyKeyGenerator from '../useIdempotencyKeyGenerator';

function clearQueryCache(queryClient: QueryClient) {
  queryClient.invalidateQueries({
    queryKey: [queryKeys.list],
  });
  queryClient.invalidateQueries({
    queryKey: [queryKeys.details],
  });
}

export const useGetTeammatesList = (
  queryParams?: TeammateListQueryParams,
  enabled = true,
) => {
  const [searchParams] = useSearchParams();
  const params = queryParams
    ? new URLSearchParams(convertObjectValuesToString(queryParams)).toString()
    : searchParams.toString();

  const { data, isPending, isSuccess } = useQuery({
    queryKey: [queryKeys.list, params],
    queryFn: () => getTeammatesListService(params),
    enabled,
  });

  return {
    isSuccess,
    teammates: data?.teammates.records || [],
    aggregateInfo: data?.aggregateInfo,
    isTeammatesPending: isPending,
    totalRecordCount: data?.teammates.totalRecordCount || 0,
  };
};

export const useGetTeammateListForSelection = (
  queryParams: TeammateForSelectionQueryParams,
  enabled = true,
) => {
  const params = new URLSearchParams(
    convertObjectValuesToString(queryParams),
  ).toString();
  const { data: teammatesList = [], isPending: isTeammatesListPending } =
    useQuery({
      queryFn: () => getTeammatesListForSelectionService(params),
      queryKey: [queryKeys.list, queryKeys.selectionList, params],
      select: data => {
        const mappedItems: SelectOption[] = data
          ? data.map(teammate => {
              return {
                ...teammate,
                value: teammate.uuid,
                label: teammate.fullName,
              };
            })
          : [];

        return mappedItems;
      },
      enabled,
      gcTime: Infinity,
      staleTime: Infinity,
    });

  return { teammatesList, isTeammatesListPending };
};

export const useGetTeammateDetails = (uuid: string) => {
  const { data, isPending } = useQuery({
    queryKey: [queryKeys.details, uuid],
    queryFn: () => getTeammateDetailsService(uuid),
    enabled: !!uuid,
  });

  return {
    teammateDetails: data,
    isTeammateDetailsPending: isPending,
  };
};

export const useResetTeammatePassword = (uuid: string) => {
  const queryClient = useQueryClient();

  const {
    mutateAsync: resetTeammatePassword,
    isPending: isResetTeammatePasswordPending,
  } = useMutation({
    mutationFn: (payload: UpdatePasswordFields) =>
      resetTeammatePasswordService({ ...payload, uuid }),
    onSuccess: () => {
      clearQueryCache(queryClient);
    },
  });

  return {
    resetTeammatePassword,
    isResetTeammatePasswordPending,
  };
};

export const useStartTeammateUpdate = (uuid: string) => {
  const { mutateAsync: startTeammateUpdate } = useMutation({
    mutationFn: () => startTeammateUpdateService(uuid),
  });

  return {
    startTeammateUpdate,
  };
};

export const useChangeTeammateEmail = (uuid: string) => {
  const queryClient = useQueryClient();

  const {
    mutateAsync: changeTeammateEmail,
    isPending: isChangeTeammateEmailPending,
  } = useMutation({
    mutationFn: (payload: UpdateEmailFields) =>
      changeTeammateEmailService({ ...payload, uuid }),
    onSuccess: () => {
      clearQueryCache(queryClient);
    },
  });

  return {
    changeTeammateEmail,
    isChangeTeammateEmailPending,
  };
};

export const useChangeTeammatePhone = (uuid: string) => {
  const queryClient = useQueryClient();

  const {
    mutateAsync: changeTeammatePhone,
    isPending: isChangeTeammatePhonePending,
  } = useMutation({
    mutationFn: (payload: UpdatePhoneFields) =>
      changeTeammatePhoneService({ ...payload, uuid }),
    onSuccess: () => {
      clearQueryCache(queryClient);
    },
  });

  return {
    changeTeammatePhone,
    isChangeTeammatePhonePending,
  };
};

export const useLockTeammate = (teammateId: string) => {
  const queryClient = useQueryClient();

  const { mutateAsync: lockTeammate, isPending: isLockTeammatePending } =
    useMutation({
      mutationFn: () => lockTeammateService(teammateId),
      onSuccess: () => {
        clearQueryCache(queryClient);
      },
    });

  return {
    lockTeammate,
    isLockTeammatePending,
  };
};

export const useUnlockTeammate = (teammateId: string) => {
  const queryClient = useQueryClient();

  const { mutateAsync: unlockTeammate, isPending: isUnlockTeammatePending } =
    useMutation({
      mutationFn: () => unlockTeammateService(teammateId),
      onSuccess: () => {
        clearQueryCache(queryClient);
      },
    });

  return {
    unlockTeammate,
    isUnlockTeammatePending,
  };
};

export const useDeactivateTeammate = (uuid: string, enabled = false) => {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending } = useMutation({
    mutationFn: (teammateId?: string) =>
      deactivateTeammateService(uuid, false, teammateId),
    onSuccess: () => clearQueryCache(queryClient),
  });
  const { data, isFetching: isCanDeactivateTeammatePending } = useQuery({
    queryKey: [queryKeys.preflight, uuid],
    queryFn: () => deactivateTeammateService(uuid, true),
    enabled,
  });

  return {
    canDeactivateTeammate: data?.mustReplaceTeammate === false || false,
    isCanDeactivateTeammatePending,
    deactivateTeammate: mutateAsync,
    isDeactivateTeammatePending: isPending,
  };
};

export const useReactivateTeammate = (teammateId: string) => {
  const queryClient = useQueryClient();

  const {
    mutateAsync: reactivateTeammate,
    isPending: isReactivateTeammatePending,
  } = useMutation({
    mutationFn: () => reactivateTeammateService(teammateId),
    onSuccess: () => {
      clearQueryCache(queryClient);
    },
  });

  return {
    reactivateTeammate,
    isReactivateTeammatePending,
  };
};

export const useAddTeammate = () => {
  const idempotencyKey = useIdempotencyKeyGenerator();
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: (data: TeammateFormFields) =>
      addTeammateService({ ...data, idempotencyKey }),
    onSuccess: () => clearQueryCache(queryClient),
  });

  return {
    addTeammate: mutateAsync,
    isAddTeammatePending: isPending,
  };
};

export const useEditTeammate = () => {
  const idempotencyKey = useIdempotencyKeyGenerator();
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: (data: TeammateFormFields) =>
      editTeammateService({ ...data, idempotencyKey }),
    onSuccess: () => clearQueryCache(queryClient),
  });

  return {
    editTeammate: mutateAsync,
    isEditTeammatePending: isPending,
  };
};
